import { FeatureTypeConfig } from "../../components/dataCatalog/config/featureTypes";
import { combinePlaceParts } from "./combinePlaceParts";
import { isImplicitTopline } from "./topline";

/**
 * Regenerates the full flattened query string from `state.search.parameters`, the same shape
 * `parametersToSegments.js` reads but producing plain text (via each type's existing
 * `generateQueryString`, already reused by the Data Catalog for this exact purpose) instead of
 * `string | {pill}` segments. Used by the results page's Sum/Average and `#`/`%` toggles (Phase
 * G): changing either isn't a display transform of already-fetched rows (Sum/Average and Count/
 * Percent are separate backend aggregations, confirmed by reading how the Data Catalog's own
 * aggregation buttons flow to `/api/search/execute`) -- it means regenerating the query with the
 * new aggregation and resubmitting through the normal search path, same as any edited search.
 *
 * `aggregationOverride(metric)`, if given, returns the aggregation to use for that metric instead
 * of its current one -- every other metric, filter, and place passes through unchanged.
 */
export function parametersToQueryText(parameters, { aggregationOverride } = {}) {
  if (!parameters) return "";

  const metricParts = (parameters.metrics?.metrics || [])
    .map((metric) =>
      aggregationOverride ? { ...metric, aggregation: aggregationOverride(metric) } : metric
    )
    .filter((metric) => !isImplicitTopline(metric))
    .map(
      (metric) => FeatureTypeConfig[metric.type]?.generateQueryString(metric) ?? metric.display
    );
  const filterParts = (parameters.filters?.filters || []).map((filter) =>
    FeatureTypeConfig.filter.generateQueryString(filter)
  );
  // See combinePlaceParts.js for de-duplicating a shared "{level plural} in " prefix across
  // multiple places drilled into the same geography level.
  const placeParts = combinePlaceParts(parameters.places || []);

  const parts = [];
  if (metricParts.length > 0) parts.push(metricParts.join(" "));
  if (filterParts.length > 0) parts.push(`among ${filterParts.join(" ")} voters`);
  if (placeParts.length > 0) parts.push(`in ${placeParts.join(" ")}`);

  return parts.join(" ");
}
